<template>
  <TopNav />
  <main>
    <!-- Show loading during MSAL initialization -->
    <div v-if="!isInitialized" class="loading-state">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p>Loading authentication...</p>
      </div>
    </div>
    <!-- Show initialization error -->
    <div v-else-if="initializationError" class="error-state">
      <div class="error-content">
        <h3>Authentication Error</h3>
        <p>{{ initializationError.message }}</p>
        <button @click="retryInitialization">Retry</button>
      </div>
    </div>
    <!-- Router view handles all routing logic including auth checks -->
    <router-view v-else />
  </main>
  
  <!-- Toast notifications for user feedback -->
  <ToastContainer />
</template>

<script setup lang="ts">
import TopNav from "@/components/TopNav.vue";
import ToastContainer from "@/components/ToastContainer.vue";
import { useAuth } from "@/composables/useAuth";
import { onMounted } from "vue";

const { 
  isInitialized, 
  initializationError, 
  initializeMsal 
} = useAuth();

// Trigger MSAL initialization when the App component mounts
onMounted(async () => {
  try {
    await initializeMsal();
  } catch (err) {
    console.error("App.vue: Initialization failed", err);
  }
});

const retryInitialization = async () => {
  try {
    await initializeMsal();
  } catch (err) {
    console.error("App.vue: Retry initialization failed", err);
  }
};
</script>

<style>
/* Global app styling following design specification */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #444444;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #FAFAFA; /* Soft neutral background */
}

main {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

/* Global typography */
body {
  margin: 0;
  padding: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background-color: #FAFAFA;
}

/* Focus outline accessibility */
*:focus {
  outline: 2px solid #0078D4;
  outline-offset: 2px;
}

/* Loading and error states */
.loading-state, .error-state {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  padding: 2rem;
}

.loading-content, .error-content {
  text-align: center;
  max-width: 400px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0078D4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-content h3 {
  color: #c00;
  margin-bottom: 0.5rem;
}

.error-content button {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  background-color: #0078D4;
  color: white;
  border: none;
  border-radius: 4px;
  margin-top: 1rem;
}

.error-content button:hover {
  background-color: #106ebe;
}
</style>
